import { useDebouncedState } from '../helpers/hooks';
import {
  useGetContractsQuery,
  useLazyGetContractsQuery,
} from '../services/edgeApi';
import { Contract } from '@/types';
import { useCallback, useEffect, useState } from 'react';

export type UseContractsOptions = {
  pageSize?: number;
  includeDetails?: boolean;
  filters?: {
    searchText?: string;
    types?: number[];
    organizationIds?: number[];
    tspIds?: number[];
    hasPoints?: boolean;
    isActiveOnDate?: boolean;
    useInScenarios?: boolean;
    contractIds?: number[];
  };
};

export const useContracts = (options: UseContractsOptions) => {
  const { pageSize = 30, includeDetails = true, filters = {} } = options;
  const {
    searchText = '',
    types,
    organizationIds,
    tspIds,
    hasPoints = undefined,
    isActiveOnDate = undefined,
    useInScenarios = undefined,
    contractIds,
  } = filters;
  const [shouldFetch, setShouldFetch] = useState<boolean>(true);
  const [exhausted, setExhausted] = useState<boolean>(false);
  const [cursor, setCursor] = useState<number>(0);
  const [contracts, setContracts] = useState<Contract[]>([]);

  const [debouncedSearch, setDebouncedSearch] = useDebouncedState(
    searchText,
    500,
  );

  useEffect(() => {
    setDebouncedSearch(searchText);
  }, [searchText]);

  useEffect(() => {
    setShouldFetch(true);
    setCursor(0);
    setExhausted(false);
    setContracts([]);
  }, [
    debouncedSearch,
    includeDetails,
    pageSize,
    types,
    organizationIds,
    tspIds,
    hasPoints,
    isActiveOnDate,
    useInScenarios,
    contractIds,
  ]);

  const { data, error, isLoading, isFetching } = useGetContractsQuery(
    {
      include_details: includeDetails,
      limit: pageSize,
      offset: cursor,
      search: debouncedSearch,
      types: types,
      organization_ids: organizationIds,
      tsp_ids: tspIds,
      has_points: hasPoints,
      is_active_on_date: isActiveOnDate,
      use_in_scenarios: useInScenarios,
      ids: contractIds,
    },
    {
      skip: !shouldFetch || exhausted,
    },
  );

  const [getContractsTrigger] = useLazyGetContractsQuery();

  if (shouldFetch && isFetching === false && exhausted === false) {
    setContracts((prevContracts) => [...prevContracts, ...(data ?? [])]);
    setShouldFetch(false);
    if (data && data.length > 0) {
      setCursor((prevCursor) => prevCursor + data.length);
    } else {
      setExhausted(true);
    }
  }

  const next = useCallback(() => {
    setShouldFetch(true);
  }, [setShouldFetch]);

  const reset = useCallback(() => {
    setShouldFetch(true);
    setCursor(0);
    setExhausted(false);
    setContracts([]);
  }, [setShouldFetch, setCursor, setExhausted, setContracts]);

  const refresh = useCallback(() => {
    // Add a slight delay to the refresh
    setTimeout(async () => {
      try {
        const result = await getContractsTrigger({
          include_details: includeDetails,
          limit: cursor,
          offset: 0,
          search: debouncedSearch,
          types: types,
          organization_ids: organizationIds,
          tsp_ids: tspIds,
          has_points: hasPoints,
          is_active_on_date: isActiveOnDate,
          use_in_scenarios: useInScenarios,
          ids: contractIds,
        }).unwrap();
        if (result) {
          setContracts(result);
        }
      } catch (error) {
        console.error('Error refreshing contracts:', error);
      }
    }, 300);
  }, [
    getContractsTrigger,
    cursor,
    includeDetails,
    debouncedSearch,
    types,
    organizationIds,
    tspIds,
    hasPoints,
    isActiveOnDate,
    useInScenarios,
    contractIds,
  ]);

  return {
    contracts,
    isLoading,
    isFetching,
    error,
    exhausted,
    next,
    reset,
    refresh,
  };
};
